import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import ExifReader from "exifreader";
import imageCompression from "browser-image-compression";
import {
  FaSchool,
  FaIdCard,
  FaMapMarkerAlt,
  FaCloudUploadAlt,
  FaVideo,
  FaArrowLeft,
} from "react-icons/fa";
import axiosConn from "config/axiosConn";
import { apiSlice } from "redux/api/apiSlice.js";
import { store } from "redux/store.js";
import { getCurrentCoords, embedGpsExif, getGeoFailureMessage } from "./gpsFallbackUtils";
import "./EkPed3Modal.css";

/*
 * Ek Ped Maa Ke Naam 3.0 — Create Event form (embedded in the fullscreen modal).
 *
 * Reuses the existing event-creation pipeline end-to-end: the `save-theme` API,
 * the `upload-image-file` / `upload-video-file` endpoints, the same geotag +
 * device-GPS-fallback logic as EventManagement, and the same validation rules
 * (plantation trees required & ≤ 100, ≥ 1 geotagged photo, participants > 0,
 * description ≤ 50 words). The Mission-LiFE classification fields are fixed and
 * submitted silently so the event lands in the Event List correctly classified.
 */

// The hidden fixed values (theme, sub-theme, mode, type, pledge, etc.) are set
// server-side in the save-ekped3 controller — the form never sends or shows them.

const MAX_MB = 10;
const MAX_IMAGES = 10; // Ek Ped 3.0 allows up to 10 geotagged photos
const SAPLING_CAP = 700; // per school, across the whole campaign
const CAMPAIGN_MIN = "2026-06-05T00:00"; // earliest allowed event date (campaign start)

// EXIF GPS → decimal degrees. Copied from EventManagement so a non-geotagged
// photo can be stamped with the device's current location (school upload).
function extractGPS(tag, refTag) {
  if (!tag) return NaN;
  let dec = typeof tag.description === "number" ? tag.description : parseFloat(tag.description);
  if (!Number.isFinite(dec) && Array.isArray(tag.value)) {
    const toNum = (x) => (Array.isArray(x) ? (x[1] ? x[0] / x[1] : x[0]) : Number(x));
    const d = toNum(tag.value[0]);
    const m = tag.value.length > 1 ? toNum(tag.value[1]) : 0;
    const s = tag.value.length > 2 ? toNum(tag.value[2]) : 0;
    dec = d + m / 60 + s / 3600;
  }
  if (!Number.isFinite(dec)) return NaN;
  // Only the first character decides the hemisphere ("EaSt longitude" must not
  // match the S and negate every east longitude).
  const ref = String(refTag?.description ?? refTag?.value ?? "").trim().toUpperCase();
  if (ref.startsWith("S") || ref.startsWith("W")) dec = -Math.abs(dec);
  return dec;
}

// ISO / Date → the value a <input type="datetime-local"> expects (YYYY-MM-DDTHH:mm),
// in local time. Returns "" when the stored date is missing / unparseable.
function toDatetimeLocal(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const TreeGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M12 2.5c3.2 0 5.5 2.3 5.5 5 0 .6-.1 1.2-.3 1.7 1.2.6 2 1.9 2 3.3 0 2.1-1.7 3.7-3.8 3.7H8.6c-2.1 0-3.8-1.6-3.8-3.7 0-1.4.8-2.7 2-3.3-.2-.5-.3-1.1-.3-1.7 0-2.7 2.3-5 5.5-5Z"
      fill="#eafaef"
    />
    <path d="M12 12v9" stroke="#eafaef" strokeWidth="2" strokeLinecap="round" />
    <path d="M12 16.5 9.5 14M12 18l2.5-2.3" stroke="#cdeccf" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

export default function EkPed3CreateForm({
  onBack,
  onSuccess,
  mode = "create",
  cap = SAPLING_CAP,
  record = null,
}) {
  const userData = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();

  const isUpdate = mode === "update";

  // Re-read the cookie identity (/me) when the form opens, so the read-only
  // "School Information" card and the record we save describe the school the
  // browser is ACTUALLY logged in as — not a stale per-tab Redux copy left over
  // after another school logged in from a second tab.
  useEffect(() => {
    const req = dispatch(apiSlice.endpoints.loadUser.initiate({}, { forceRefetch: true }));
    return () => { req?.unsubscribe?.(); };
  }, [dispatch]);

  const [trees, setTrees] = useState("");
  const [desc, setDesc] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [age3to8, setAge3to8] = useState("0");
  const [age8to11, setAge8to11] = useState("0");
  const [age11to14, setAge11to14] = useState("0");
  const [age14to18, setAge14to18] = useState("0");
  const [age18plus, setAge18plus] = useState("0");
  const [city, setCity] = useState("");
  const [photos, setPhotos] = useState([]); // [{ photo_url, latitude, longitude }]
  const [videos, setVideos] = useState([]); // [url]
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // The school's recorded total at load (0 in create mode). The count can be
  // raised up to the 700 cap but never reduced below this baseline.
  const [baseline, setBaseline] = useState(0);
  // In update mode, the school's latest 3.0 record (cumulative trees + the most
  // recent event's details) fetched from `ekpedmaakenaam3-latest` for prefill —
  // unless the caller already handed a `record` prop down.
  const [autoRecord, setAutoRecord] = useState(null);
  const treeFloor = isUpdate ? Math.min(baseline, cap) : 0; // can't go below this
  const treeMinEntry = Math.max(1, treeFloor); // smallest valid entry

  // Click a photo / video to view it enlarged. null = closed.
  const [lightbox, setLightbox] = useState(null); // { type: 'image'|'video', src }

  // Validation / submit error shown INLINE inside the modal. Toasts render behind
  // the full-screen popup, so users never saw them — this banner is always visible.
  const [formError, setFormError] = useState("");
  const errorRef = useRef(null);
  useEffect(() => {
    if (formError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [formError]);

  // In update mode, fetch the school's latest 3.0 record so the form can pre-fill
  // (cumulative trees + the most recent event's date/city/participants/media).
  // The popup gate only tells us a record EXISTS (via the count endpoint); the
  // full record for prefill lives behind `ekpedmaakenaam3-latest`. Skipped when a
  // `record` prop was already provided. UDISE resolves server-side from the session.
  useEffect(() => {
    if (!isUpdate || record) return;
    let active = true;
    (async () => {
      try {
        const res = await axiosConn("post", "ekpedmaakenaam3-latest", {}, {
          "Content-Type": "application/json",
        });
        if (active && res?.data?.success && res.data.record) {
          setAutoRecord(res.data.record);
        }
      } catch (err) {
        console.warn("Ek Ped 3.0 prefill fetch failed:", err?.message);
      }
    })();
    return () => {
      active = false;
    };
  }, [isUpdate, record]);

  // Pre-fill from the `record` prop when given, otherwise from the fetched
  // `autoRecord`. Trees + participants are cumulative totals; date/city/media come
  // from the most recent event.
  useEffect(() => {
    const src = record || autoRecord;
    if (!isUpdate || !src) return;
    setTrees(src.trees != null && src.trees !== "" ? String(src.trees) : "");
    setBaseline(Number(src.trees) || 0);
    setDesc(src.desc || "");
    // Only pre-fill the date if it's within the campaign window; an older legacy
    // date (e.g. before 5 June) would be rejected by the field, so leave it blank.
    const d = toDatetimeLocal(src.event_date);
    setEventDate(d && d >= CAMPAIGN_MIN ? d : "");
    setAge3to8(String(src.number_participant_age3to8 ?? "0"));
    setAge8to11(String(src.number_participant_age8to11 ?? "0"));
    setAge11to14(String(src.number_participant_age11to14 ?? "0"));
    setAge14to18(String(src.number_participant_age14to18 ?? "0"));
    setAge18plus(String(src.number_participant_age18plus ?? "0"));
    setCity(src.city || "");
    if (Array.isArray(src.photos)) {
      setPhotos(
        src.photos
          .map((p) => ({ photo_url: p.photo_url, latitude: p.latitude ?? null, longitude: p.longitude ?? null }))
          .filter((p) => p.photo_url)
      );
    }
    if (Array.isArray(src.videos)) setVideos(src.videos.filter(Boolean));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdate, record, autoRecord]);

  const schoolName = userData?.school_name || "—";
  const udise = userData?.udise_id || "—";
  const stateName = userData?.state || "";
  const districtName = userData?.district || "";

  const clampTree = (raw) => {
    const cleaned = String(raw).replace(/[^0-9]/g, "");
    if (cleaned === "") return "";
    let n = parseInt(cleaned, 10);
    if (n > cap) n = cap; // hard max 700 on entry
    return String(n);
  };
  const stepTree = (delta) => {
    const current = parseInt(trees || "0", 10) || 0;
    let next = current + delta;
    if (next < treeMinEntry) next = treeMinEntry; // can't step below the baseline
    if (next > cap) next = cap;
    setTrees(String(next));
  };

  const onAgeChange = (setter) => (e) => {
    const cleaned = e.target.value.replace(/[^0-9]/g, "");
    setter(cleaned === "" ? "" : String(parseInt(cleaned, 10)));
  };

  // ── Photo upload (geotag + device-GPS fallback) ────────────────────────────
  const handlePhotoFiles = async (e) => {
    try {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;
      const allowed = ["png", "jpeg", "jpg"];
      const remaining = MAX_IMAGES - photos.length;
      if (remaining <= 0) {
        toast.warning(`You have already uploaded the maximum ${MAX_IMAGES} images`);
        e.target.value = "";
        return;
      }
      const toUpload = files.slice(0, remaining);
      if (files.length > remaining) {
        toast.warning(`Only ${remaining} more image(s) can be uploaded for this event`);
      }

      setUploadingPhoto(true);
      const formData = new FormData();
      const payload = [];
      let deviceCoords = null;
      const getFallbackCoords = async () => {
        if (!deviceCoords) deviceCoords = await getCurrentCoords();
        return deviceCoords;
      };

      for (const file of toUpload) {
        const ext = file.type.split("/")[1];
        if (!allowed.includes(ext)) {
          toast.warning("Only PNG, JPEG and JPG files are allowed");
          e.target.value = "";
          setUploadingPhoto(false);
          return;
        }

        let tags = {};
        try {
          tags = ExifReader.load(await file.arrayBuffer());
        } catch (exifErr) {
          console.warn("Failed to read EXIF:", exifErr);
        }

        let workingFile = file;
        let latitude = extractGPS(tags.GPSLatitude, tags.GPSLatitudeRef);
        let longitude = extractGPS(tags.GPSLongitude, tags.GPSLongitudeRef);

        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
          let coords;
          try {
            coords = await getFallbackCoords();
          } catch (geoErr) {
            toast.warning(await getGeoFailureMessage(geoErr));
            e.target.value = "";
            setUploadingPhoto(false);
            return;
          }
          latitude = coords.latitude;
          longitude = coords.longitude;
          try {
            workingFile = await embedGpsExif(file, latitude, longitude);
          } catch (embedErr) {
            console.warn("Could not write GPS EXIF into the image:", embedErr);
          }
          toast.info("This photo was not geo-tagged, so your current location has been added to it.");
        }

        const dateStr = new Date().toISOString().replace(/[:.]/g, "-");
        const outExt = workingFile.type.split("/")[1];
        const newName = `${file.name}_${dateStr}_ekped3.${outExt}`;
        let processedFile;
        if (workingFile.size > MAX_MB * 1024 * 1024) {
          const blob = await imageCompression(workingFile, {
            maxSizeMB: MAX_MB,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          });
          processedFile = new File([blob], newName, { type: workingFile.type });
        } else {
          processedFile = new File([workingFile], newName, { type: workingFile.type });
        }

        formData.append("files", processedFile);
        formData.append("latitude", latitude);
        formData.append("longitude", longitude);
        payload.push({ latitude, longitude });
      }

      const res = await axiosConn("post", "upload-image-file", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.status === "success") {
        const uploaded = res.data.data;
        const newPhotos = uploaded.map((url, i) => ({
          photo_url: url,
          latitude: payload[i]?.latitude ?? null,
          longitude: payload[i]?.longitude ?? null,
        }));
        setPhotos((prev) => [...prev, ...newPhotos]);
      } else {
        toast.warning("Failed to upload files");
      }
      e.target.value = "";
    } catch (err) {
      console.error(err);
      toast.error("Unexpected error while uploading images");
      e.target.value = "";
    } finally {
      setUploadingPhoto(false);
    }
  };

  // ── Video upload (≤ 5 MB each, max 3) ──────────────────────────────────────
  const handleVideoFiles = async (e) => {
    try {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;
      const allowedMimes = ["video/mp4", "video/webm", "video/quicktime"];
      const allowedExt = ["mp4", "webm", "mov"];
      const remaining = 3 - videos.length;
      if (remaining <= 0) {
        toast.warning("You have already uploaded the maximum 3 videos");
        e.target.value = "";
        return;
      }
      const toUpload = files.slice(0, remaining);
      if (files.length > remaining) {
        toast.warning(`Only ${remaining} more video(s) can be uploaded for this event`);
      }

      const formData = new FormData();
      for (const file of toUpload) {
        const ext = (file.name.split(".").pop() || "").toLowerCase();
        if (!allowedMimes.includes(file.type) && !allowedExt.includes(ext)) {
          toast.warning("Only video files (MP4, WEBM, MOV) are allowed");
          e.target.value = "";
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          toast.warning("Each video must be less than 5 MB");
          e.target.value = "";
          return;
        }
        formData.append("files", file);
      }

      setUploadingVideo(true);
      const res = await axiosConn("post", "upload-video-file", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.status === "success") {
        setVideos((prev) => [...prev, ...res.data.data]);
      } else {
        toast.warning("Failed to upload videos");
      }
      e.target.value = "";
    } catch (err) {
      console.error(err);
      toast.error("Unexpected error while uploading videos");
      e.target.value = "";
    } finally {
      setUploadingVideo(false);
    }
  };

  const removePhoto = (idx) => setPhotos((prev) => prev.filter((_, i) => i !== idx));
  const removeVideo = (idx) => setVideos((prev) => prev.filter((_, i) => i !== idx));

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const treeNum = parseInt(trees, 10);
    const totalParticipants =
      (parseInt(age3to8, 10) || 0) +
      (parseInt(age8to11, 10) || 0) +
      (parseInt(age11to14, 10) || 0) +
      (parseInt(age14to18, 10) || 0) +
      (parseInt(age18plus, 10) || 0);

    const warnings = [
      { condition: !trees || Number.isNaN(treeNum) || treeNum < 1, message: "Please enter number of trees planted." },
      { condition: treeNum > cap, message: `Per-school total of ${cap} saplings is the maximum.` },
      { condition: isUpdate && treeNum < treeFloor, message: `The plantation count can only be increased — it can't be reduced below ${treeFloor}.` },
      { condition: !desc.trim(), message: "Please enter a brief description." },
      { condition: desc.trim().split(/\s+/).length > 50, message: "Description should not be more than 50 words." },
      { condition: !eventDate, message: "Please select the date and time." },
      { condition: [age3to8, age8to11, age11to14, age14to18, age18plus].some((v) => Number(v) >= 5000), message: "Each participant count must be less than 5000." },
      { condition: totalParticipants <= 0, message: "Please enter the number of participants." },
      { condition: !city.trim(), message: "Please enter City." },
      { condition: city.trim() && !/^[a-zA-Z][a-zA-Z0-9\s.'&-]*$/.test(city.trim()), message: "Please enter a valid city name." },
      { condition: photos.length === 0, message: "Please upload at least one geotagged photo." },
    ];
    setFormError("");
    for (const { condition, message } of warnings) {
      if (condition) {
        setFormError(message); // shown inline (toasts render behind the modal)
        return;
      }
    }

    setSubmitting(true);
    try {
      // Confirm the login identity right before saving. If another tab switched
      // this browser to a different school, stop here instead of writing this
      // school's plantation into that school's record (the server also refuses
      // such a save with 409, this just gives a clearer message).
      const norm = (v) => String(v ?? "").replace(/\D/g, "").replace(/^0+/, "");
      const shownUdise = norm(userData?.udise_id);
      let liveUser = userData;
      try {
        await dispatch(apiSlice.endpoints.loadUser.initiate({}, { forceRefetch: true }));
        liveUser = store.getState().auth.user || userData;
      } catch (e) { /* the server-side guard still protects the save */ }
      if (shownUdise && norm(liveUser?.udise_id) !== shownUdise) {
        setFormError(
          `This browser is now logged in as ${liveUser?.school_name || "another school"} (UDISE ${liveUser?.udise_id || "—"}). ` +
          "The form has been switched to that school — please review the details and submit again."
        );
        setSubmitting(false);
        return;
      }

      // One endpoint owns Ek Ped 3.0: it sets the fixed classification values,
      // enforces the 700 cap, and creates or updates the school's single record
      // (folding in any duplicates) — the client just sends the entered details.
      // Identity fields are informational: the server takes them from the cookie.
      const postData = {
        udise_id: liveUser?.udise_id,
        school_name: liveUser?.school_name,
        desc: desc.trim(),
        event_date: eventDate,
        number_participant_age3to8: age3to8 || "0",
        number_participant_age8to11: age8to11 || "0",
        number_participant_age11to14: age11to14 || "0",
        number_participant_age14to18: age14to18 || "0",
        number_participant_age18plus: age18plus || "0",
        number_of_trees_Planted: treeNum,
        state: stateName,
        district: districtName,
        city: city.trim(),
        photos,
        videos,
        assigned_teacher: [userData?.email],
        auther: userData?.email,
      };

      const response = await axiosConn("post", "save-ekped3", postData, {
        "Content-Type": "application/json",
      }, dispatch);

      if (response.data.status === "success") {
        setFormError("");
        toast.success(
          isUpdate
            ? "Ek Ped Maa Ke Naam 3.0 details updated successfully!"
            : "Ek Ped Maa Ke Naam 3.0 details submitted successfully!"
        );
        onSuccess && onSuccess();
      } else {
        setFormError(response.data.message || "Failed to submit. Please try again.");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || "An error occurred during submission. Please try again.";
      setFormError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ekped3-form-inner">
      <div className="ekped3-form-header">
        <button type="button" className="ekped3-form-back" onClick={onBack}>
          <FaArrowLeft size={13} /> Back to Selection
        </button>
        <h1 className="ekped3-form-title">Ek Ped Maa Ke Naam 3.0</h1>
      </div>

      {/* School Information — compact, read-only, single row */}
      <div className="ekped3-school-card">
        <div className="ekped3-school-card-title">
          <FaSchool /> School Information Profile (Read-Only)
        </div>
        <div className="ekped3-school-row">
          <div className="ekped3-school-item">
            <span className="ekped3-school-icon"><FaSchool /></span>
            <span>
              <span className="label">School Name</span>
              <span className="value" title={schoolName}>{schoolName}</span>
            </span>
          </div>
          <div className="ekped3-school-item">
            <span className="ekped3-school-icon"><FaIdCard /></span>
            <span>
              <span className="label">UDISE Code</span>
              <span className="value">{udise}</span>
            </span>
          </div>
          <div className="ekped3-school-item">
            <span className="ekped3-school-icon"><FaMapMarkerAlt /></span>
            <span>
              <span className="label">State / District</span>
              <span className="value" title={`${stateName}/${districtName}`}>
                {stateName}{stateName && districtName ? "/" : ""}{districtName}
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Form */}
      <form className="ekped3-form-panel" onSubmit={handleSubmit}>
        <div className="ekped3-grid-2">
          {/* No. of Trees Planted — premium stepper */}
          <div className="ekped3-tree-wrap">
            <label className="ekped3-field" style={{ marginBottom: 7 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#2c3f34" }}>
                No. of Trees Planted <span className="ekped3-req">*</span>
              </span>
            </label>
            <div className="ekped3-tree">
              <div className="ekped3-tree-badge"><TreeGlyph /></div>
              <input
                className="ekped3-tree-input"
                type="number"
                inputMode="numeric"
                min={treeMinEntry}
                max={cap}
                placeholder="0"
                value={trees}
                onChange={(e) => setTrees(clampTree(e.target.value))}
              />
              <div className="ekped3-tree-steppers">
                <button type="button" className="ekped3-step-btn" aria-label="Increase" onClick={() => stepTree(1)}>▲</button>
                <button type="button" className="ekped3-step-btn" aria-label="Decrease" onClick={() => stepTree(-1)}>▼</button>
              </div>
            </div>
            <span className="ekped3-hint">
              {isUpdate
                ? `Your total is pre-filled. You can increase it up to ${cap}; it can't be reduced.`
                : `Enter the total number of saplings planted (max ${cap}).`}
            </span>
          </div>

          {/* Brief Description — sits where the event name used to be */}
          <div className="ekped3-field">
            <label>Brief Description Of Event (Not More Than 50 Words) <span className="ekped3-req">*</span></label>
            <textarea
              className="ekped3-textarea"
              rows={2}
              placeholder="Brief Description Of Event (Not More Than 50 Words)"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
            />
          </div>

          {/* Date and Time */}
          <div className="ekped3-field">
            <label>Date and Time <span className="ekped3-req">*</span></label>
            <input
              className="ekped3-input"
              type="datetime-local"
              min="2026-06-05T00:00"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
            />
          </div>
        </div>

        {/* Participants */}
        <div className="ekped3-section">
          <h3 className="ekped3-section-title">Number of Participants</h3>
          <div className="ekped3-section-rule" />
        </div>

        <div className="ekped3-grid-3">
          <div className="ekped3-field">
            <label>Age 3 to 8 <span className="ekped3-req">*</span></label>
            <input className="ekped3-input no-spin" type="number" min="0" value={age3to8} onChange={onAgeChange(setAge3to8)} />
          </div>
          <div className="ekped3-field">
            <label>Age 8 to 11 <span className="ekped3-req">*</span></label>
            <input className="ekped3-input no-spin" type="number" min="0" value={age8to11} onChange={onAgeChange(setAge8to11)} />
          </div>
          <div className="ekped3-field">
            <label>Age 11 to 14 <span className="ekped3-req">*</span></label>
            <input className="ekped3-input no-spin" type="number" min="0" value={age11to14} onChange={onAgeChange(setAge11to14)} />
          </div>
          <div className="ekped3-field">
            <label>Age 14 to 18 <span className="ekped3-req">*</span></label>
            <input className="ekped3-input no-spin" type="number" min="0" value={age14to18} onChange={onAgeChange(setAge14to18)} />
          </div>
          <div className="ekped3-field">
            <label>Age 18 plus <span className="ekped3-req">*</span></label>
            <input className="ekped3-input no-spin" type="number" min="0" value={age18plus} onChange={onAgeChange(setAge18plus)} />
          </div>
          <div className="ekped3-field">
            <label>City <span className="ekped3-req">*</span></label>
            <input className="ekped3-input" type="text" placeholder="Enter city" value={city} onChange={(e) => setCity(e.target.value)} />
          </div>
        </div>

        {/* Uploads */}
        <div className="ekped3-grid-2" style={{ marginTop: 20 }}>
          <div className="ekped3-field">
            <label>Geotagged photos (Max {MAX_IMAGES} images) <span className="ekped3-req">*</span></label>
            <label className={`ekped3-upload ${photos.length ? "has-files" : ""}`}>
              <input type="file" accept="image/png,image/jpeg,image/jpg" multiple onChange={handlePhotoFiles} disabled={uploadingPhoto || photos.length >= MAX_IMAGES} />
              <span className="ekped3-upload-btn"><FaCloudUploadAlt style={{ marginRight: 6 }} />Choose Files</span>
              <span className="ekped3-upload-text">
                {uploadingPhoto ? "Uploading…" : photos.length ? `${photos.length} image(s) added` : "No file chosen"}
              </span>
            </label>
            {photos.length > 0 && (
              <div className="ekped3-thumbs">
                {photos.map((p, i) => (
                  <div className="ekped3-thumb" key={i}>
                    <img
                      src={p.photo_url}
                      alt={`Uploaded ${i + 1}`}
                      onClick={() => setLightbox({ type: "image", src: p.photo_url })}
                      title="Click to view"
                    />
                    <button type="button" onClick={() => removePhoto(i)} aria-label="Remove">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="ekped3-field">
            <label>Upload Videos (Max 3 Videos, 5MB each)</label>
            <label className={`ekped3-upload ${videos.length ? "has-files" : ""}`}>
              <input type="file" accept="video/mp4,video/webm,video/quicktime" multiple onChange={handleVideoFiles} disabled={uploadingVideo || videos.length >= 3} />
              <span className="ekped3-upload-btn"><FaVideo style={{ marginRight: 6 }} />Choose Files</span>
              <span className="ekped3-upload-text">
                {uploadingVideo ? "Uploading…" : videos.length ? `${videos.length} video(s) added` : "No file chosen"}
              </span>
            </label>
            {videos.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {videos.map((v, i) => (
                  <span className="ekped3-vid-chip" key={i}>
                    <button
                      type="button"
                      className="ekped3-vid-open"
                      onClick={() => setLightbox({ type: "video", src: v })}
                      title="Click to play"
                    >
                      <FaVideo size={11} /> Video {i + 1}
                    </button>
                    <button type="button" onClick={() => removeVideo(i)} aria-label="Remove">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {formError && (
          <div className="ekped3-form-error" role="alert" ref={errorRef}>
            <span className="ekped3-form-error-icon">!</span>
            <span>{formError}</span>
          </div>
        )}

        <div className="ekped3-submit-row">
          <button type="submit" className="ekped3-submit" disabled={submitting || uploadingPhoto || uploadingVideo}>
            {submitting ? "Submitting…" : isUpdate ? "Update" : "Submit"}
          </button>
        </div>
      </form>

      {/* Enlarged image / video viewer */}
      {lightbox && (
        <div className="ekped3-lightbox" onClick={() => setLightbox(null)}>
          <button type="button" className="ekped3-lightbox-close" aria-label="Close" onClick={() => setLightbox(null)}>×</button>
          {lightbox.type === "image" ? (
            <img src={lightbox.src} alt="Preview" onClick={(e) => e.stopPropagation()} />
          ) : (
            <video src={lightbox.src} controls autoPlay onClick={(e) => e.stopPropagation()} />
          )}
        </div>
      )}
    </div>
  );
}
